<template>
  <DeepDialog
    v-bind="$attrs"
    v-model="model"
    :max-width="480"
    :title="t('deepa_chat.context_dialog_title', nodes.length)"
  >
    <template #content>
      <v-text-field
        v-if="nodes.length > FILTER_THRESHOLD"
        v-model="filter"
        class="mb-2"
        clearable
        density="compact"
        hide-details
        :placeholder="t('deepa_chat.context_filter')"
        prepend-inner-icon="far fa-magnifying-glass"
        variant="outlined"
      />
      <v-virtual-scroll
        :height="Math.min(filteredNodes.length * ITEM_HEIGHT, 400)"
        :item-height="ITEM_HEIGHT"
        :items="filteredNodes"
      >
        <template #default="{ item }">
          <v-list-item
            density="compact"
            :height="ITEM_HEIGHT"
            slim
            :to="getContextNodeRoute(item)"
            @click="model = false"
          >
            <template #prepend>
              <NodeIcon
                class="me-3"
                height="20"
                :mime-type="item.mimeType"
                :name="item.name"
                :type="item.type"
                width="20"
              />
            </template>
            <v-list-item-title>
              {{ getContextNodeName(item) }}
            </v-list-item-title>
          </v-list-item>
        </template>
      </v-virtual-scroll>
    </template>
  </DeepDialog>
</template>

<script lang="ts" setup>
import {
  getContextNodeRoute,
  useDeepAContextNodeName,
} from '@/pocs/chat/composables/context-node'
import { DeepDialog } from '@deepcloud/deep-ui-lib'
import NodeIcon from '@/components/node/NodeIcon.vue'
import type { DeepAChatContextNode } from '@/pocs/chat/types'

const FILTER_THRESHOLD = 10
const ITEM_HEIGHT = 40

const props = defineProps({
  nodes: {
    type: Array as PropType<DeepAChatContextNode[]>,
    default: () => [],
  },
})

const model = defineModel({ type: Boolean, default: false })

const { t } = useI18n()
const getContextNodeName = useDeepAContextNodeName()

const filter = ref<string | null>('')

const filteredNodes = computed(() => {
  const term = filter.value?.trim().toLowerCase()
  if (!term) return props.nodes
  return props.nodes.filter((node) =>
    getContextNodeName(node).toLowerCase().includes(term),
  )
})

watch(model, (isOpen) => {
  if (!isOpen) {
    filter.value = ''
  }
})
</script>
